import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    LinkedinShareButton,
    LinkedinIcon,
    WhatsappShareButton,
    WhatsappIcon,
    FacebookShareButton,
    FacebookIcon,
    XShareButton,
    XIcon,
    TelegramShareButton,
    TelegramIcon,
    RedditShareButton,
    RedditIcon,
    EmailShareButton,
    EmailIcon,
} from "react-share";
import { Share2, X, Copy, Check } from "lucide-react";
export default function ShareModal({
    isOpen,
    onClose,
    url = window.location.href,
    title = "Check this out",
    description = "",
}) {
    const [copied, setCopied] = useState(false);
    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => {
                setCopied(false);
            }, 2000);
        } catch (error) {
            console.error("Failed to copy URL:", error);
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <div
                    className="fixed inset-0 z-[9999] flex items-center justify-center px-4"
                    onClick={onClose}
                >
                    {/* Overlay */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute inset-0 bg-[#000036]/60 backdrop-blur-sm"
                    />
                    {/* Modal */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.94, y: 20, }}
                        animate={{ opacity: 1, scale: 1, y: 0, }}
                        exit={{ opacity: 0, scale: 0.94, y: 20, }}
                        transition={{
                            duration: 0.25,
                            ease: "easeOut",
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden border border-slate-200"
                    >
                        {/* Header */}
                        <div className="relative px-6 sm:px-7 pt-6 pb-5 border-b border-slate-100">
                            <div className="absolute -right-10 -top-10 w-32 h-32 rounded-full bg-[#5DC9F0]/10 blur-2xl" />
                            <div className="relative flex items-start justify-between gap-4">
                                <div>
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="w-10 h-10 rounded-lg bg-[#000036] flex items-center justify-center">
                                            <Share2
                                                size={20}
                                                className="text-[#fff]"
                                            />
                                        </span>
                                    </div>
                                    <h3 className="text-xl sm:text-2xl font-bold text-[#000036]">
                                        Share this
                                    </h3>
                                    <p className="mt-1 text-base text-gray-600">
                                        Share it with your network.
                                    </p>
                                </div>
                                {/* Close */}
                                <button
                                    onClick={onClose}
                                    className="w-9 h-9 shrink-0 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-500 hover:text-[#000036] transition-colors"
                                    aria-label="Close share dialog"
                                >
                                    <X size={17} />
                                </button>
                            </div>
                        </div>
                        {/* Content */}
                        <div className="px-6 sm:px-7 py-6">
                            {/* Social platforms */}
                            <p className="text-sm uppercase tracking-[0.18em] font-bold text-slate-400 mb-4">
                                Share via
                            </p>
                            <div className="grid grid-cols-4 gap-3">

                                {/* LinkedIn */}
                                <LinkedinShareButton
                                    url={url}
                                    title={title}
                                    summary={description}
                                >
                                    <ShareItem
                                        icon={<LinkedinIcon size={42} round />}
                                        label="LinkedIn"
                                    />
                                </LinkedinShareButton>

                                {/* WhatsApp */}
                                <WhatsappShareButton
                                    url={url}
                                    title={title}
                                >
                                    <ShareItem
                                        icon={<WhatsappIcon size={42} round />}
                                        label="WhatsApp"
                                    />
                                </WhatsappShareButton>

                                {/* Facebook */}
                                <FacebookShareButton
                                    url={url}
                                    quote={title}
                                >
                                    <ShareItem
                                        icon={<FacebookIcon size={42} round />}
                                        label="Facebook"
                                    />
                                </FacebookShareButton>

                                {/* X */}
                                <XShareButton
                                    url={url}
                                    title={title}
                                >
                                    <ShareItem
                                        icon={<XIcon size={42} round />}
                                        label="X"
                                    />
                                </XShareButton>

                                {/* Telegram */}
                                <TelegramShareButton
                                    url={url}
                                    title={title}
                                >
                                    <ShareItem
                                        icon={<TelegramIcon size={42} round />}
                                        label="Telegram"
                                    />
                                </TelegramShareButton>

                                {/* Reddit */}
                                <RedditShareButton
                                    url={url}
                                    title={title}
                                >
                                    <ShareItem
                                        icon={<RedditIcon size={42} round />}
                                        label="Reddit"
                                    />
                                </RedditShareButton>

                                {/* Email */}
                                <EmailShareButton
                                    url={url}
                                    subject={title}
                                    body={description}
                                >
                                    <ShareItem
                                        icon={<EmailIcon size={42} round />}
                                        label="Email"
                                    />
                                </EmailShareButton>
                            </div>
                            {/* Divider */}
                            <div className="flex items-center gap-3 my-6">
                                <div className="h-px bg-slate-200 flex-1" />
                                <span className="text-[9px] uppercase tracking-widest font-bold text-slate-300">
                                    or
                                </span>
                                <div className="h-px bg-slate-200 flex-1" />
                            </div>
                            {/* Copy link */}
                            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400 mb-2">
                                Copy link
                            </p>
                            <div className="flex items-center gap-2 p-1.5 rounded-xl bg-slate-50 border border-slate-200">

                                <div className="flex-1 min-w-0 px-3">
                                    <p className="text-xs text-slate-500 truncate">
                                        {url}
                                    </p>
                                </div>

                                <button
                                    onClick={handleCopy}
                                    className={`shrink-0 flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-xs font-bold transition-all duration-200
                                     ${copied
                                            ? "bg-emerald-500 text-white"
                                            : "bg-[#000036] text-white hover:bg-[#11114d]"
                                        }`}>
                                    {copied ? (
                                        <>
                                            <Check size={14} />
                                            Copied
                                        </>
                                    ) : (
                                        <>
                                            <Copy size={14} />
                                            Copy
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="px-6 sm:px-7 py-4 bg-slate-50 border-t border-slate-100">
                            <p className="text-center text-sm text-gray-600">
                                Share this with someone who might find it useful.
                            </p>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}
function ShareItem({ icon, label }) {
    return (
        <div className="w-full flex flex-col items-center gap-2 group">
            <div className="w-12 h-12 rounded-2xl bg-slate-50 group-hover:bg-slate-100 flex items-center justify-center transition-all group-hover:-translate-y-1">
                {icon}
            </div>
            <span className="text-[12px] font-semibold text-slate-500">
                {label}
            </span>
        </div>
    );
}